import { ref, computed } from 'vue';
import { clearAppSession, saveAppSession } from '@/utils/appSession';

// ───────────────────────────────────────────────────────────────────────────
// Acceso de demo: una persona del equipo (staff con `demo.access`) entra una
// vez con SUS credenciales del backoffice y puede usar la app como los
// perfiles demo del catálogo, sin conocer sus contraseñas.
// Contrato: docs/BE-RESPONSE-DEMO-ACCESS.md.
//
//   POST /api/demo/session          { email, password } → demoToken + personas
//   GET  /api/demo/personas         x-demo-token        → personas
//   POST /api/demo/session/login    x-demo-token, { personaId } → login normal + `demo`
//   POST /api/demo/session/logout   x-demo-token        → corta la demo (y sus tokens)
//
// Las llamadas van con fetch y NO con la instancia de axios: su interceptor
// trata cualquier 401 como sesión vencida, y aquí un 401 puede ser solo
// "contraseña incorrecta" o "demo vencida" (lo maneja la pantalla).
// El demoToken vive en sessionStorage (8 h; se pierde al cerrar la pestaña).
// ───────────────────────────────────────────────────────────────────────────

const KEYS = {
  token: 'gme.demo.token',
  session: 'gme.demo.session', // { expiresAt, staffName }
  persona: 'gme.demo.persona', // bloque `demo` del último login como perfil
};

const read = (k) => { try { return JSON.parse(sessionStorage.getItem(k) || 'null'); } catch { return null; } };
const write = (k, v) => { try { sessionStorage.setItem(k, typeof v === 'string' ? v : JSON.stringify(v)); } catch { /* sin storage */ } };

export const getDemoToken = () => { try { return sessionStorage.getItem(KEYS.token); } catch { return null; } };
export const hasDemoSession = () => !!getDemoToken();

/** Borra la sesión de demo de esta pestaña (no llama al API). */
export const clearDemoSession = () => {
  ['token', 'session', 'persona'].forEach((k) => { try { sessionStorage.removeItem(KEYS[k]); } catch { /* */ } });
};

/**
 * Logout de la app estando en una demo = salir de la demo: revoca el demoToken
 * en el API (y con él los tokens de perfil que emitió) y deja la pestaña sin
 * rastro. El candado del login no depende de esto: solo sale con ?demo=1.
 * Es fire-and-forget (keepalive) porque el logout recarga la página enseguida.
 */
export const leaveDemo = () => {
  const token = getDemoToken();
  if (token) {
    try {
      fetch(`${process.env.VUE_APP_API}/api/demo/session/logout`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', 'x-demo-token': token },
      }).catch(() => {});
    } catch { /* sin fetch */ }
  }
  clearDemoSession();
};

const api = async (path, { method = 'GET', body } = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = getDemoToken();
  if (token) headers['x-demo-token'] = token;
  let res;
  try {
    res = await fetch(`${process.env.VUE_APP_API}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  } catch {
    return { ok: false, code: 'NETWORK_ERROR' };
  }
  let json = {};
  try { json = await res.json(); } catch { /* sin cuerpo */ }
  if (!res.ok || json.success === false) return { ok: false, status: res.status, code: json.code || 'SERVER_ERROR' };
  return { ok: true, json };
};

// Estado compartido (un solo selector abierto a la vez).
const personas = ref([]);
const session = ref(read(KEYS.session));
const persona = ref(read(KEYS.persona));

export const useDemoAccess = () => {
  const loading = ref(false);
  const error = ref(''); // código del API

  const isExpired = computed(() => !session.value?.expiresAt || new Date(session.value.expiresAt) <= new Date());

  /** Credenciales de staff → abre la sesión de demo y trae los perfiles. */
  const start = async (email, password) => {
    loading.value = true; error.value = '';
    const r = await api('/api/demo/session', { method: 'POST', body: { email, password } });
    loading.value = false;
    if (!r.ok) { error.value = r.code; return false; }
    const d = r.json.data || {};
    write(KEYS.token, d.demoToken);
    session.value = { expiresAt: d.expiresAt, staffName: d.staff?.name || '' };
    write(KEYS.session, session.value);
    personas.value = d.personas || [];
    return true;
  };

  /** Perfiles disponibles con la sesión actual. `false` si la demo ya no sirve. */
  const loadPersonas = async () => {
    if (!hasDemoSession()) return false;
    loading.value = true; error.value = '';
    const r = await api('/api/demo/personas');
    loading.value = false;
    if (!r.ok) {
      error.value = r.code;
      if (r.status === 401) clearDemoSession();
      return false;
    }
    personas.value = r.json.data?.personas || [];
    return true;
  };

  /**
   * Entra como un perfil: guarda la sesión como un login normal y devuelve el
   * usuario (para elegir el dashboard). Antes borra la del perfil anterior.
   */
  const loginAs = async (personaId) => {
    loading.value = true; error.value = '';
    const r = await api('/api/demo/session/login', { method: 'POST', body: { personaId } });
    loading.value = false;
    if (!r.ok) {
      error.value = r.code;
      if (r.status === 401) clearDemoSession();
      return null;
    }
    clearAppSession();
    saveAppSession(r.json);
    persona.value = r.json.demo || null;
    write(KEYS.persona, persona.value);
    return r.json.user || {};
  };

  /** Cierra la demo: corta el demoToken y los tokens de la app que se emitieron con él. */
  const end = async () => {
    if (hasDemoSession()) await api('/api/demo/session/logout', { method: 'POST' });
    clearDemoSession();
    clearAppSession();
    session.value = null;
    persona.value = null;
    personas.value = [];
  };

  return { personas, session, persona, loading, error, isExpired, start, loadPersonas, loginAs, end };
};
